import { useState } from'react';
import { motion, AnimatePresence } from'framer-motion';
import { Eye, Circle } from'lucide-react';

export default function Step3() {
 const [eyeCount, setEyeCount] = useState(72);
 const [selectedWheelInfo, setSelectedWheelInfo] = useState<string | null>(null);

 const wheelInfoData = [
 {
 id:'outer',
 title:'Outer Wheel',
 hebrew:'האופן החיצון',
 description:'The largest ring, representing the cosmic scope of divine awareness. Its eyes see all of creation.',
 eyeCount: 32,
 color:'#FBBF24'
 },
 {
 id:'middle',
 title:'Wheel Within Wheel',
 hebrew:'אופן בתוך האופן',
 description:'The mysterious inner wheel that intersects the outer, suggesting multiple dimensions of reality.',
 eyeCount: 25,
 color:'#F97316'
 },
 {
 id:'inner',
 title:'Inner Wheel',
 hebrew:'האופן הפנימי',
 description:'The innermost circle, closest to the divine fire, representing intimate providence.',
 eyeCount: 15,
 color:'#7C3AED'
 }
 ];

 const eyeCountOptions = [
 { value: 36, label:'36', description:'Tzadik' },
 { value: 72, label:'72', description:'Divine Names' },
 { value: 108, label:'108', description:'Sacred' },
 { value: 144, label:'144', description:'Complete' },
 ];

 return (
 <div className="section-container">
 {/* Introduction */}
 <div className="max-w-4xl mx-auto mb-16">
 <motion.div
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 className="text-center mb-12"
 >
 <p className="hebrew-text text-2xl md:text-3xl text-primary-600 dark:text-primary-400 mb-4">
 וגביהן וגבה להם ויראה להם וגבתם מלאת עינים סביב
 </p>
 <p className="text-lg text-[var(--color-muted)] italic">
"As for their rims, they were tall and awesome,
 and the rims of all four were full of eyes all around."
 </p>
 <p className="text-sm text-[var(--color-muted)] mt-2">- Ezekiel 1:18</p>
 </motion.div>

 <motion.div
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: 0.2 }}
 className="prose prose-lg dark:prose-invert mx-auto"
 >
 <p>
 The Ophanim (wheels) are among the most mysterious elements of the Merkavah vision.
 Described as wheels within wheels, covered with countless eyes, they represent
 divine omniscience and the interconnected nature of all existence.
 </p>
 </motion.div>
 </div>

 {/* Wheel Visualization Card */}
 <motion.div
 initial={{ opacity: 0, scale: 0.9 }}
 animate={{ opacity: 1, scale: 1 }}
 transition={{ delay: 0.3 }}
 className="mb-16"
 >
 <h2 className="text-2xl md:text-3xl font-display font-bold text-center mb-4">
 The <span className="text-flame">Ophanim</span> - Wheels of Vision
 </h2>
 <p className="text-center text-[var(--color-muted)] mb-8 max-w-xl mx-auto">
 The all-seeing wheels of divine awareness
 </p>

 {/* Stylized wheel representation using cards */}
 <div className="vision-container max-w-xl mx-auto p-8">
 <div className="relative">
 {/* Outer ring */}
 <div className="w-64 h-64 mx-auto relative rounded-full border-4 border-amber-500/50 flex items-center justify-center bg-gradient-to-br from-amber-900/20 to-orange-900/20">
 {/* Middle ring */}
 <div className="w-44 h-44 rounded-full border-4 border-orange-500/50 flex items-center justify-center bg-gradient-to-br from-orange-900/20 to-violet-900/20">
 {/* Inner ring */}
 <div className="w-24 h-24 rounded-full border-4 border-violet-500/50 flex items-center justify-center bg-gradient-to-br from-violet-900/30 to-amber-900/30">
 {/* Center glow */}
 <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
 <Eye className="w-6 h-6 text-white" />
 </div>
 </div>
 </div>

 {/* Eyes on outer ring */}
 {[...Array(8)].map((_, i) => {
 const angle = (i / 8) * Math.PI * 2;
 const x = Math.cos(angle) * 110;
 const y = Math.sin(angle) * 110;
 return (
 <motion.div
 key={`outer-${i}`}
 initial={{ opacity: 0 }}
 animate={{ opacity: 1 }}
 transition={{ delay: 0.5 + i * 0.05 }}
 className="absolute w-4 h-4 rounded-full bg-gradient-to-br from-white to-amber-200 border border-amber-500"
 style={{
 left: `calc(50% + ${x}px - 8px)`,
 top: `calc(50% + ${y}px - 8px)`,
 }}
 >
 <div className="w-1.5 h-1.5 rounded-full bg-amber-900 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
 </motion.div>
 );
 })}

 {/* Eyes on middle ring */}
 {[...Array(6)].map((_, i) => {
 const angle = (i / 6) * Math.PI * 2 + 0.3;
 const x = Math.cos(angle) * 75;
 const y = Math.sin(angle) * 75;
 return (
 <motion.div
 key={`middle-${i}`}
 initial={{ opacity: 0 }}
 animate={{ opacity: 1 }}
 transition={{ delay: 0.7 + i * 0.05 }}
 className="absolute w-3 h-3 rounded-full bg-gradient-to-br from-white to-orange-200 border border-orange-500"
 style={{
 left: `calc(50% + ${x}px - 6px)`,
 top: `calc(50% + ${y}px - 6px)`,
 }}
 >
 <div className="w-1 h-1 rounded-full bg-orange-900 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
 </motion.div>
 );
 })}
 </div>

 {/* Labels */}
 <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-full pb-2">
 <p className="text-xs text-[#3366cc] font-medium">Outer Wheel</p>
 </div>
 </div>
 </div>

 {/* Eye Count Control */}
 <div className="max-w-3xl mx-auto mt-8">
 <p className="text-center text-sm text-[var(--color-muted)] mb-4">
 Select the number of <span className="hebrew-text">עינים</span> (eyes):
 </p>
 <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
 {eyeCountOptions.map((option) => (
 <button
 key={option.value}
 onClick={() => setEyeCount(option.value)}
 className={`p-4 rounded-xl border-2 transition-all duration-300 hover:shadow-lg ${
 eyeCount === option.value
 ?'border-primary-600 bg-primary-600/20 shadow-lg shadow-primary-600/20'
 :'border-[var(--color-border)] hover:border-primary-600/50 hover:bg-primary-600/5'
 }`}
 >
 <span className={`block text-3xl font-bold ${
 eyeCount === option.value ?'text-primary-600 dark:text-primary-400' :''
 }`}>
 {option.label}
 </span>
 <span className="block text-xs text-[var(--color-muted)] mt-1">
 {option.description}
 </span>
 </button>
 ))}
 </div>
 <p className="text-center text-sm text-[var(--color-muted)] mt-4">
 Currently showing: <span className="font-semibold text-primary-600 dark:text-primary-400">{eyeCount}</span> all-seeing eyes
 </p>
 </div>
 </motion.div>

 {/* Wheel Information Sections */}
 <motion.div
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: 0.4 }}
 className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16"
 >
 {wheelInfoData.map((info, index) => (
 <motion.button
 key={info.id}
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: 0.5 + index * 0.1 }}
 onClick={() => setSelectedWheelInfo(selectedWheelInfo === info.id ? null : info.id)}
 className={`card text-left transition-all duration-300 hover:shadow-lg ${
 selectedWheelInfo === info.id ?'ring-2 ring-primary-500' :''
 }`}
 >
 <div className="flex items-center gap-3 mb-3">
 <div
 className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300"
 style={{ backgroundColor: `${info.color}30` }}
 >
 <Circle className="w-5 h-5" style={{ color: info.color }} />
 </div>
 <div>
 <h3 className="text-lg font-semibold">{info.title}</h3>
 <p className="hebrew-text text-sm" style={{ color: info.color }}>
 {info.hebrew}
 </p>
 </div>
 </div>
 <p className="text-sm text-[var(--color-muted)]">{info.description}</p>
 <div className="mt-3 text-xs text-[var(--color-muted)]">
 <Eye className="w-3 h-3 inline mr-1" style={{ color: info.color }} />
 {info.eyeCount} eyes on this ring
 </div>
 </motion.button>
 ))}
 </motion.div>

 {/* Symbolism Section */}
 <motion.div
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: 0.5 }}
 className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16"
 >
 <div className="card transition-all duration-300 hover:shadow-lg">
 <h3 className="text-xl font-semibold mb-4">Wheels Within Wheels</h3>
 <p className="hebrew-text text-lg text-primary-600 dark:text-primary-400 mb-4">
 אופן בתוך האופן
 </p>
 <p className="text-[var(--color-muted)]">
 The image of a wheel within a wheel suggests multiple dimensions of reality
 interpenetrating each other. It represents the idea that divine providence
 operates on multiple levels simultaneously - what appears as random chance
 from one perspective is seen as purposeful design from another.
 </p>
 </div>

 <div className="card transition-all duration-300 hover:shadow-lg">
 <h3 className="text-xl font-semibold mb-4">The Many Eyes</h3>
 <p className="hebrew-text text-lg text-primary-600 dark:text-primary-400 mb-4">
 מלאת עינים
 </p>
 <p className="text-[var(--color-muted)]">
 The wheels covered with eyes symbolize divine omniscience - the all-seeing
 awareness that perceives everything in creation. Nothing is hidden from
 this gaze, which sees not only actions but intentions, not only the surface
 but the depths.
 </p>
 </div>
 </motion.div>

 {/* Movement Description */}
 <motion.div
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: 0.6 }}
 className="mb-16"
 >
 <h2 className="text-2xl md:text-3xl font-display font-bold text-center mb-8">
 Movement of the <span className="text-[#202122] font-bold font-serif">Wheels</span>
 </h2>

 <div className="max-w-3xl mx-auto">
 <div className="card p-8 transition-all duration-300 hover:shadow-lg">
 <blockquote className="text-center mb-8">
 <p className="text-lg italic text-[var(--color-muted)]">
"When the living creatures moved, the wheels beside them moved;
 and when the living creatures rose from the ground, the wheels also rose.
 Wherever the spirit would go, they would go... for the spirit of the
 living creatures was in the wheels."
 </p>
 <footer className="mt-4 text-sm text-primary-600 dark:text-primary-400">
 - Ezekiel 1:19-21
 </footer>
 </blockquote>

 <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
 <div className="p-4 rounded-lg bg-primary-600/10 transition-all duration-300 hover:bg-primary-600/20">
 <h4 className="font-semibold mb-2">Unified Movement</h4>
 <p className="text-sm text-[var(--color-muted)]">
 The wheels moved in perfect sync with the living creatures,
 demonstrating the unity of divine action.
 </p>
 </div>
 <div className="p-4 rounded-lg bg-secondary-700/10 transition-all duration-300 hover:bg-secondary-700/20">
 <h4 className="font-semibold mb-2">Any Direction</h4>
 <p className="text-sm text-[var(--color-muted)]">
 The wheels could move in any of four directions without turning,
 suggesting divine presence everywhere.
 </p>
 </div>
 <div className="p-4 rounded-lg bg-primary-600/10 transition-all duration-300 hover:bg-primary-600/20">
 <h4 className="font-semibold mb-2">Spirit Within</h4>
 <p className="text-sm text-[var(--color-muted)]">
 The spirit of the living creatures inhabited the wheels,
 animating and directing their motion.
 </p>
 </div>
 </div>
 </div>
 </div>
 </motion.div>

 {/* Tarshish Appearance */}
 <motion.div
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: 0.7 }}
 className="p-8 rounded-2xl bg-gradient-to-r from-primary-600/10 to-secondary-700/10 border border-primary-600/20 mb-16"
 >
 <h3 className="text-xl font-semibold mb-4 text-center">
 The Appearance of Tarshish
 </h3>
 <p className="hebrew-text text-lg text-center text-primary-600 dark:text-primary-400 mb-4">
 מראה האופנים כעין תרשיש
 </p>
 <p className="text-center text-[var(--color-muted)] max-w-2xl mx-auto">
 The wheels had the appearance of"tarshish" - often translated as chrysolite or beryl.
 This precious stone, with its golden-green iridescence, suggests the wheels shimmered
 with inner light, reflecting the divine radiance they bore. Some traditions identify
 tarshish with the sea, suggesting depths of meaning beyond human comprehension.
 </p>
 </motion.div>

 {/* Angelic Category */}
 <motion.div
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: 0.8 }}
 >
 <h2 className="text-2xl md:text-3xl font-display font-bold text-center mb-8">
 Ophanim as <span className="text-flame">Angelic Beings</span>
 </h2>

 <div className="max-w-3xl mx-auto card transition-all duration-300 hover:shadow-lg">
 <p className="text-[var(--color-muted)] mb-6">
 In later Jewish tradition, the Ophanim came to be understood not merely as wheels
 but as a category of angelic beings. In the Merkavah literature and later Kabbalah,
 they are part of the celestial hierarchy:
 </p>

 <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center mb-6">
 <div className="p-4 rounded-lg bg-[var(--color-background)] transition-all duration-300 hover:shadow-md">
 <h4 className="font-semibold text-primary-600 dark:text-primary-400">Chayot</h4>
 <p className="text-sm text-[var(--color-muted)]">Highest rank, bear the throne</p>
 </div>
 <div className="p-4 rounded-lg bg-[var(--color-background)] ring-2 ring-primary-500 transition-all duration-300 hover:shadow-md">
 <h4 className="font-semibold text-primary-600 dark:text-primary-400">Ophanim</h4>
 <p className="text-sm text-[var(--color-muted)]">Wheels, all-seeing awareness</p>
 </div>
 <div className="p-4 rounded-lg bg-[var(--color-background)] transition-all duration-300 hover:shadow-md">
 <h4 className="font-semibold text-primary-600 dark:text-primary-400">Seraphim</h4>
 <p className="text-sm text-[var(--color-muted)]">Burning ones, Isaiah's vision</p>
 </div>
 </div>

 <p className="text-[var(--color-muted)] text-center">
 Together, these three orders of celestial beings continuously declare:
 </p>
 <p className="hebrew-text text-2xl text-center kedushah-text mt-4 mb-2">
 קדוש קדוש קדוש ה׳ צבאות
 </p>
 <p className="text-sm italic text-center text-[var(--color-muted)]">
"Holy, holy, holy is the Lord of Hosts"
 </p>
 </div>
 </motion.div>

 {/* Mathematical Structure */}
 <motion.div
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: 0.9 }}
 className="mt-12 p-8 rounded-2xl bg-gradient-to-r from-secondary-700/10 to-primary-600/10 border border-secondary-700/20"
 >
 <h3 className="text-xl font-semibold mb-6 text-center">
 Sacred Numbers of the Ophanim
 </h3>

 <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 text-center">
 <div className="transition-all duration-300 hover:scale-105">
 <div className="text-4xl font-bold text-flame mb-2">4</div>
 <p className="text-sm text-[var(--color-muted)]">Wheels</p>
 <p className="text-xs text-[var(--color-muted)] italic mt-1">One beside each Chayot</p>
 </div>
 <div className="transition-all duration-300 hover:scale-105">
 <div className="text-4xl font-bold text-[#202122] font-bold font-serif mb-2">72</div>
 <p className="text-sm text-[var(--color-muted)]">Divine Names</p>
 <p className="text-xs text-[var(--color-muted)] italic mt-1">Traditional eye count</p>
 </div>
 <div className="transition-all duration-300 hover:scale-105">
 <div className="text-4xl font-bold text-flame mb-2">2</div>
 <p className="text-sm text-[var(--color-muted)]">Wheels in One</p>
 <p className="text-xs text-[var(--color-muted)] italic mt-1">Intersecting dimensions</p>
 </div>
 <div className="transition-all duration-300 hover:scale-105">
 <div className="text-4xl font-bold text-[#202122] font-bold font-serif mb-2">&infin;</div>
 <p className="text-sm text-[var(--color-muted)]">Eyes</p>
 <p className="text-xs text-[var(--color-muted)] italic mt-1">Infinite awareness</p>
 </div>
 </div>
 </motion.div>
 </div>
 );
}
